import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV_ITEMS = [
  { label: "Program", href: "#program" },
  { label: "Platform", href: "#platform" },
  { label: "Educators", href: "#educators" },
  { label: "Partners", href: "#partners" },
  { label: "Demo", href: "#platform" },
];

/**
 * LandingNav — sticky glassmorphic top bar. Hamburger → bottom-sheet
 * on mobile. Logo, anchor links, Login, Get Started CTA.
 *
 * Designed to feel premium-quiet at rest and snap into focus when the
 * user starts scrolling (border darkens, shadow appears).
 */
export default function LandingNav({ onGetStarted }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goAnchor = (href) => {
    setOpen(false);
    if (href.startsWith("#")) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <nav
      aria-label="Primary"
      className={`sticky top-0 z-40 w-full backdrop-blur-xl transition-all duration-300 ${
        scrolled
          ? "bg-white/85 border-b border-black/10 shadow-[0_4px_24px_rgba(0,0,0,0.06)]"
          : "bg-white/55 border-b border-transparent"
      }`}
      data-testid="landing-nav"
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-6 h-16 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-2.5 shrink-0"
          aria-label="Lemonade Stand Business Plan — home"
          data-testid="landing-nav-logo"
        >
          <img
            src="/assets/lsbp-logo.png"
            alt=""
            className="h-9 w-9 sm:h-10 sm:w-10"
          />
          <span className="hidden sm:inline font-black text-[15px] tracking-tight text-black leading-none">
            Lemonade Stand
            <span className="block text-[10px] font-bold tracking-[0.18em] uppercase text-gray-500 mt-0.5">
              Business Plan
            </span>
          </span>
        </Link>

        {/* Desktop nav */}
        <ul className="hidden lg:flex items-center gap-7">
          {NAV_ITEMS.map((it) => (
            <li key={it.label}>
              <button
                onClick={() => goAnchor(it.href)}
                className="text-base font-bold text-gray-700 hover:text-black tracking-wide transition-colors"
                data-testid={`landing-nav-${it.label.toLowerCase()}`}
              >
                {it.label}
              </button>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={() => navigate("/login")}
            className="hidden sm:inline-flex text-base font-bold text-gray-800 hover:text-black px-3 py-2 transition-colors"
            data-testid="landing-nav-login"
          >
            Login
          </button>
          <button
            onClick={onGetStarted}
            className="bg-black text-white px-4 sm:px-5 py-2.5 rounded-full text-base font-bold tracking-wide hover:bg-[#FFD500] hover:text-black transition-colors shadow-[0_4px_14px_rgba(0,0,0,0.18)]"
            data-testid="landing-nav-get-started"
          >
            Get Started
          </button>
          <button
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? "Close menu" : "Open menu"}
            className="lg:hidden p-2 -mr-2 text-black"
            data-testid="landing-nav-hamburger"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile sheet */}
      {open && (
        <div className="lg:hidden border-t border-black/10 bg-white" data-testid="landing-nav-mobile-sheet">
          <ul className="max-w-7xl mx-auto px-4 py-3 space-y-1">
            {NAV_ITEMS.map((it) => (
              <li key={it.label}>
                <button
                  onClick={() => goAnchor(it.href)}
                  className="w-full text-left py-3 px-3 rounded-xl text-base font-bold text-gray-900 hover:bg-yellow-50"
                >
                  {it.label}
                </button>
              </li>
            ))}
            <li>
              <button
                onClick={() => navigate("/login")}
                className="w-full text-left py-3 px-3 rounded-xl text-base font-bold text-gray-700"
              >
                Login
              </button>
            </li>
          </ul>
        </div>
      )}
    </nav>
  );
}
